import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Icon, Popup } from "semantic-ui-react";
import { SemanticCOLORS, SemanticICONS } from "semantic-ui-react/dist/commonjs/generic";

import CONFIG from "config/config";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import {
  MeLabReportSummary,
  MeLabVideoStatus,
  RunSequence,
  VIDEO_STATUS_NOT_FOUND,
} from "react-lib/apps/HISV3/LAB/sequence/LabReport";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

import ModMeLabPdfPreview from "./ModMeLabPdfPreview";
import ModMeLabVideoPreview from "./ModMeLabVideoPreview";

// ======================= Constants =======================
const ACTION = "GENERATE_MELAB_VIDEO";
const BUTTON_KEY = "ButtonGenMeLabVideo_GENERATE";
const PDF_ACTION = "GENERATE_MELAB_PDF";
const PDF_BUTTON_KEY = "ButtonGenMeLabVideo_GENERATE_PDF";
const V5_ACTION = "GENERATE_MELAB_VIDEO_V5";
const GOTO_MEGEN_ACTION = "GOTO_MEGEN_PROJECT";
const STATUS_ACTION = "FETCH_MELAB_VIDEO_STATUS";
const STATUS_COMPLETED = "complete";
const STATUS_FAILED = new Set(["error", "failed", "forbidden"]);
const CREATE_SET_ACTION = "CREATE_MELAB_REPORT_SET";
const CREATE_SET_KEY = "ButtonGenMeLabVideo_CREATE_SET";

// ======================= Types =======================
type ButtonGenMeLabVideoProps = {
  card: string;
  setProp: (key: string, value: any) => any;
  runSequence: RunSequence;
  // data
  labOrderItems: any[];
  labOrdersReported: any[];
  patientInfo?: Record<string, any>;
  selectedLabOrderWorking?: Record<string, any>;
  meLabReport?: MeLabReportSummary | null;
  meLabVideoStatus?: Record<string, MeLabVideoStatus>;
  buttonLoadCheck?: Record<string, any>;
};

// ======================= Helpers =======================
const formatRawStep = (step: string) =>
  step.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase());

const flatten = (items: any[]) =>
  (items || []).flatMap((item: any) => (item.children?.length ? item.children : [item]));

// ======================= Component =======================
const ButtonGenMeLabVideo = (props: ButtonGenMeLabVideoProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [openCreateSetConfirm, setOpenCreateSetConfirm] = useState(false);
  const [openGenMenu, setOpenGenMenu] = useState(false);
  const [hasVideoFile, setHasVideoFile] = useState<boolean | null>(null);

  // Show: มีรายการที่ authorize แล้วอย่างน้อย 1 (ไม่สนใจ chk)
  const hasAuthorizedItems = useMemo(
    () => flatten(props.labOrderItems).some((item: any) => item?.central_lab_result_is_authorized),
    [props.labOrderItems],
  );

  // Enable: เลือก checkbox + รายการนั้น authorize แล้ว
  const hasCheckedAuthorizedItems = useMemo(
    () =>
      flatten(props.labOrdersReported).some((item: any) => item?.central_lab_result_is_authorized),
    [props.labOrdersReported],
  );

  // report ของ lab order นี้ — sequence query ด้วย central_lab_order_id มาให้ตรง ๆ แล้ว
  const currentVideo = props.meLabReport || null;

  const currentStatus = currentVideo ? props.meLabVideoStatus?.[currentVideo.report_id] : undefined;

  // ดึง status ราย report ทุก 15 วิ ระหว่างที่ยัง gen ไม่เสร็จ
  useEffect(() => {
    const reportId = currentVideo?.report_id;

    if (
      !reportId ||
      currentStatus?.status === STATUS_COMPLETED ||
      currentStatus?.status === VIDEO_STATUS_NOT_FOUND ||
      STATUS_FAILED.has(currentStatus?.status ?? "")
    ) {
      return;
    }

    const fetchStatus = () =>
      props.runSequence({
        sequence: "LabReport",
        action: STATUS_ACTION,
        reportId,
        onSettled: () => setStatusChecked(true),
      });

    fetchStatus();

    const intervalId = setInterval(fetchStatus, 15000);

    return () => clearInterval(intervalId);
  }, [currentVideo?.report_id, currentVideo?.video_output_url, currentStatus?.status]);

  useEffect(() => {
    setStatusChecked(false);
  }, [currentVideo?.report_id]);

  const isVideoFailed = STATUS_FAILED.has(currentStatus?.status ?? "");

  // 404 = ยังไม่เคยสั่ง gen (report ถูกสร้างมาจากปุ่ม PDF), error = gen พัง — ทั้งคู่ให้กดสร้างได้
  const hasNoVideo =
    (currentStatus?.status === VIDEO_STATUS_NOT_FOUND || isVideoFailed) &&
    !currentVideo?.video_output_url;

  // status ชนะ video_output_url เสมอ เพราะ backend ไม่ล้าง url เก่าตอน gen ทับ
  // ถ้าเชื่อ url อย่างเดียวปุ่มจะค้างที่ "ดู Video" ทั้งที่รอบใหม่กำลังสร้างอยู่
  // ยกเว้น not_found/error ที่ไม่ได้บอกอะไรเรื่อง video เก่า จึงถอยไปดู url แทน
  const hasLiveStatus =
    !!currentStatus && currentStatus.status !== VIDEO_STATUS_NOT_FOUND && !isVideoFailed;
  const isVideoDone = hasLiveStatus
    ? currentStatus.status === STATUS_COMPLETED
    : !!currentVideo?.video_output_url;

  const isGeneratingVideo = props.buttonLoadCheck?.[BUTTON_KEY] === "LOADING";

  const hasPdf = !!currentVideo?.pdf_output_url;
  const isPdfApproved = !!currentVideo?.pdf_approved_at;
  // ตอนสร้างทับ ปุ่มที่เห็นคือ "ดู PDF" ซึ่งไม่ผูกกับ key ของ gen เลยต้องเช็คเองไม่งั้นดูเหมือนไม่มีอะไรเกิดขึ้น
  const isGeneratingPdf = props.buttonLoadCheck?.[PDF_BUTTON_KEY] === "LOADING";

  const megenProjectId = currentStatus?.megen_project_id ?? null;
  // "pending" = ฝั่ง AI ยังไม่ผูก thread ให้ ⇒ ไปได้แค่หน้า project
  const megenThreadId =
    currentStatus?.thread_id && currentStatus.thread_id !== "pending"
      ? currentStatus.thread_id
      : undefined;

  // ทุกปุ่มยิง payload ชุดเดียวกัน ต่างแค่ action / buttonLoadKey / ตัวเลือกท้าย
  // (CREATE_MELAB_REPORT_SET ไม่สนใจ reportId — handler สร้าง report ใบใหม่เสมอ)
  const runGenSequence = useCallback(
    (action: string, buttonLoadKey: string, extra?: Record<string, any>) => {
      props.runSequence({
        sequence: "LabReport",
        action,
        card: props.card,
        buttonLoadKey,
        labOrdersReported: props.labOrdersReported,
        patientInfo: props.patientInfo,
        selectedLabOrderWorking: props.selectedLabOrderWorking,
        // ใช้ report เดิมถ้ามีแล้ว จะได้ไม่สร้าง report ซ้ำ
        reportId: currentVideo?.report_id,
        ...extra,
      });
    },
    [
      props.card,
      props.labOrdersReported,
      props.patientInfo,
      props.selectedLabOrderWorking,
      currentVideo?.report_id,
    ],
  );

  useEffect(() => {
    const reportId = currentVideo?.report_id;

    if (!reportId || !isVideoDone || !CONFIG.ENABLE_MELAB_VIDEO_V5) {
      setHasVideoFile(null);

      return;
    }

    props.runSequence({
      sequence: "LabReport",
      action: "FETCH_MELAB_VIDEO_DOWNLOAD",
      reportId,
      onSuccess: () => setHasVideoFile(true),
      onError: () => setHasVideoFile(false),
    });
  }, [currentVideo?.report_id, isVideoDone]);

  const handleGenVideo = useCallback(
    (forceNew?: boolean) => runGenSequence(ACTION, BUTTON_KEY, { forceNew }),
    [runGenSequence],
  );

  const handleGenPdf = useCallback(
    () => runGenSequence(PDF_ACTION, PDF_BUTTON_KEY),
    [runGenSequence],
  );

  const handleGenVideoV5 = useCallback(
    (forceNew?: boolean, audioOnly?: boolean) =>
      runGenSequence(V5_ACTION, BUTTON_KEY, { forceNew, audioOnly }),
    [runGenSequence],
  );

  const handleGotoMeGen = useCallback(() => {
    if (!megenProjectId) {
      return;
    }

    props.runSequence({
      sequence: "LabReport",
      action: GOTO_MEGEN_ACTION,
      projectId: megenProjectId,
      threadId: megenThreadId,
    });
  }, [megenProjectId, megenThreadId]);

  const handleCreateSet = useCallback(
    () =>
      runGenSequence(CREATE_SET_ACTION, CREATE_SET_KEY, {
        onSuccess: () => setOpenCreateSetConfirm(false),
      }),
    [runGenSequence],
  );

  if (!hasAuthorizedItems) {
    return null;
  }

  // ปุ่ม gen ตัวเดียวคุมทั้งวิดีโอปกติ (v4) และวิดีโอจากคลัง (v5) — สอง flow ใช้ report/status ก้อนเดียวกัน
  // แยกเป็นสองปุ่มจะได้ปุ่มหมุนซ้อนกันตอน gen เพราะอ่าน status ตัวเดียวกัน
  const renderGenMenuItem = (option: {
    basic?: boolean;
    color: SemanticCOLORS;
    icon: SemanticICONS;
    label: string;
    onClick: () => void;
  }) => (
    <Button
      basic={option.basic}
      size="small"
      color={option.color}
      style={{ margin: 0 }}
      onClick={() => {
        setOpenGenMenu(false);
        option.onClick();
      }}
    >
      <Icon name={option.icon} />
      {option.label}
    </Button>
  );

  const renderGenButton = () => {
    // 0. ยังไม่รู้ผล → "ตรวจสอบสถานะ..." ครอบ 2 กรณี
    //    - ยังไม่ได้ fetch report เลย
    //    - ได้ report แล้วแต่ video_output_url ว่าง ซึ่งอาจแปลว่าเสร็จแล้วก็ได้ ต้องรอ status ยืนยันก่อน
    //      ไม่งั้นจะโชว์ "กำลังสร้าง" แวบหนึ่งแล้วเด้งเป็น "ดู Video"
    if (
      props.meLabReport === undefined ||
      (currentVideo && !currentVideo.video_output_url && !statusChecked && !isGeneratingVideo)
    ) {
      return (
        <Button
          disabled
          size="medium"
          color="grey"
          style={{
            width: "100%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            minWidth: "max-content",
          }}
        >
          <Icon name="circle notched" loading style={{ margin: 0 }} />
          <span>ตรวจสอบสถานะ...</span>
        </Button>
      );
    }

    // 1. มี report แต่ยัง gen ไม่เสร็จ และไม่ได้กำลังยิง request → "กำลังสร้าง Video..."
    if (currentVideo && !isVideoDone && !isGeneratingVideo && !hasNoVideo && !isVideoFailed) {
      const stepLabel = currentStatus?.current_step ? formatRawStep(currentStatus.current_step) : "";
      const stepTooltip = [stepLabel, currentStatus?.current_step_detail]
        .filter(Boolean)
        .join(" · ");

      return (
        <Popup
          content={stepTooltip}
          disabled={!stepTooltip}
          trigger={
            <span style={{ width: "100%", minWidth: "max-content" }}>
              <Button
                disabled
                size="medium"
                color="grey"
                style={{
                  width: "100%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  minWidth: "max-content",
                }}
              >
                <Icon name="circle notched" loading style={{ margin: 0 }} />
                <span>กำลังสร้าง Video...</span>
              </Button>
            </span>
          }
        />
      );
    }

    const isApproved = !!currentVideo?.approved_at;
    // gen เสร็จแต่ไม่มีไฟล์วิดีโอ = สร้างแบบฟังเสียงก่อน ⇒ พาไปจอ MeGen แทนที่จะเปิด modal วิดีโอเปล่า
    const audioOnlyDone = isVideoDone && hasVideoFile === false && !!megenProjectId;

    // 2. ButtonLoadCheck ตัวเดียว paramKey เดียว — ห้ามแยกเป็นคนละ instance
    // เพราะ cleanup ตอน unmount จะ setProp ล้าง LOADING ทิ้งเอง
    const handleClick = audioOnlyDone
      ? handleGotoMeGen
      : isVideoDone
        ? () => setOpenModal(true)
        : CONFIG.ENABLE_MELAB_VIDEO_V5
          ? () => setOpenGenMenu(true)
          : () => handleGenVideo();

    const viewTitle = isApproved ? "ดู Video (อนุมัติแล้ว)" : "ดู Video";

    const button = (
      <ButtonLoadCheck
        setProp={props.setProp}
        onClick={handleClick}
        paramKey={BUTTON_KEY}
        buttonLoadCheck={props.buttonLoadCheck?.[BUTTON_KEY]}
        disabled={!currentVideo && !hasCheckedAuthorizedItems}
        size="medium"
        name={ACTION}
        color={audioOnlyDone ? "violet" : isVideoDone ? (isApproved ? "olive" : "green") : "blue"}
        style={{ width: "100%", minWidth: "max-content" }}
        title={
          isGeneratingVideo
            ? "กำลังสร้าง Video..."
            : audioOnlyDone
              ? "ฟังเสียงใน MeGen"
              : isVideoDone
                ? viewTitle
                : "สร้าง Video"
        }
      />
    );

    // ปิด config = ปุ่มเดี่ยวแบบเดิม กดแล้วได้วิดีโอปกติ (v4) ไม่มีเมนูให้เลือก
    if (isVideoDone || !CONFIG.ENABLE_MELAB_VIDEO_V5) {
      return button;
    }

    return (
      <Popup
        on="click"
        position="bottom center"
        open={openGenMenu}
        trigger={button}
        onClose={() => setOpenGenMenu(false)}
        onOpen={() => setOpenGenMenu(true)}
        content={
          <div style={{ display: "grid", gap: "0.5rem", minWidth: "12rem" }}>
            {renderGenMenuItem({
              color: "blue",
              icon: "film",
              label: "วิดีโอปกติ",
              onClick: () => handleGenVideo(),
            })}

            {renderGenMenuItem({
              color: "violet",
              icon: "comment",
              label: "วิดีโอจากคลัง",
              onClick: () => handleGenVideoV5(),
            })}

            {renderGenMenuItem({
              basic: true,
              color: "violet",
              icon: "volume up",
              label: "ฟังเสียงก่อน",
              onClick: () => handleGenVideoV5(false, true),
            })}

            {/* มี run แล้วเข้าไปดู/แก้ต่อได้เลย ไม่ต้องรอวิดีโอเสร็จ (เคสฟังเสียงก่อนจะยังไม่มีวิดีโอ) */}
            {!!megenProjectId &&
              renderGenMenuItem({
                color: "purple",
                icon: "external",
                label: "แก้ไขใน MeGen",
                onClick: handleGotoMeGen,
              })}
          </div>
        }
      />
    );
  };

  const renderPdfButton = () => {
    // ยังไม่ได้ fetch report → ซ่อนไว้ก่อน ปล่อยให้ฝั่ง video บอกสถานะพอ
    if (props.meLabReport === undefined) {
      return null;
    }

    return (
      <>
        <ButtonLoadCheck
          setProp={props.setProp}
          onClick={hasPdf ? () => setOpenPdfModal(true) : handleGenPdf}
          paramKey={PDF_BUTTON_KEY}
          buttonLoadCheck={props.buttonLoadCheck?.[PDF_BUTTON_KEY]}
          disabled={!currentVideo && !hasCheckedAuthorizedItems}
          size="medium"
          name="GENERATE_MELAB_PDF"
          color={isPdfApproved ? "olive" : "teal"}
          style={{ width: "100%", minWidth: "max-content" }}
          title={
            isGeneratingPdf
              ? "กำลังสร้าง PDF..."
              : hasPdf
                ? isPdfApproved
                  ? "ดู PDF (อนุมัติแล้ว)"
                  : "ดู PDF"
                : "สร้าง PDF"
          }
        />

        {hasPdf && (
          <ModMeLabPdfPreview
            open={openPdfModal}
            card={props.card}
            report={currentVideo}
            patientBirthdate={props.patientInfo?.birthdate}
            runSequence={props.runSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            onClose={() => setOpenPdfModal(false)}
            onRegenerate={() => {
              setOpenPdfModal(false);
              handleGenPdf();
            }}
          />
        )}
      </>
    );
  };

  return (
    <div style={{ display: "flex", gap: "5px" }}>
      {renderGenButton()}
      {renderPdfButton()}

      {isVideoDone && (
        <ModMeLabVideoPreview
          open={openModal}
          card={props.card}
          video={currentVideo}
          runSequence={props.runSequence}
          buttonLoadCheck={props.buttonLoadCheck}
          onClose={() => setOpenModal(false)}
          onRegenerate={() => {
            setOpenModal(false);
            handleGenVideo(true);
          }}
          onRegenerateFromLibrary={() => {
            setOpenModal(false);
            handleGenVideoV5(true);
          }}
          onGotoMeGen={
            megenProjectId
              ? () => {
                  setOpenModal(false);
                  handleGotoMeGen();
                }
              : undefined
          }
        />
      )}

      {/* โผล่เมื่อมี report อยู่แล้ว + ติ๊กรายการแลปอย่างน้อย 1 (ยังไม่มี report ให้ใช้ปุ่ม "สร้าง Video/PDF") */}
      {!!currentVideo && hasCheckedAuthorizedItems && (
        <>
          <Button
            size="medium"
            color="orange"
            style={{ width: "100%", minWidth: "max-content" }}
            onClick={() => setOpenCreateSetConfirm(true)}
          >
            สร้าง Video &amp; PDF ใหม่
          </Button>

          <ModConfirm
            openModal={openCreateSetConfirm}
            centered
            titleName="สร้าง Video และ PDF ใหม่อีกครั้ง"
            size="mini"
            approveButtonText="สร้างใหม่"
            approveButtonColor="orange"
            denyButtonText="ยกเลิก"
            content={
              <div style={{ margin: "0.5rem 0" }}>
                สร้าง Video และ PDF ชุดใหม่จากรายการที่เลือกไว้ตอนนี้ใช่หรือไม่
                <br />
                ชุดเดิมจะยังอยู่ในระบบแต่หน้านี้จะแสดงชุดใหม่แทน
              </div>
            }
            approveButtonLoading={props.buttonLoadCheck?.[CREATE_SET_KEY] === "LOADING"}
            onApprove={handleCreateSet}
            onDeny={() => setOpenCreateSetConfirm(false)}
            onCloseWithDimmerClick={() => setOpenCreateSetConfirm(false)}
          />
        </>
      )}
    </div>
  );
};

ButtonGenMeLabVideo.displayName = "ButtonGenMeLabVideo";

export default React.memo(ButtonGenMeLabVideo);
